import type { ReactElement } from 'react';
import type { ApiKeyDto } from '@gr/core/accounts/identity/api-keys';
import { useT } from '../../../../app/di';
import { formatDateTime } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { DataTable, type DataColumn } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { isApiKeyActive } from '../model/api-keys';
import styles from './ApiKeyTable.module.scss';

export interface ApiKeyTableProps {
  keys: readonly ApiKeyDto[];
  /** У администратора — колонка владельца ключа (IAM-UC-11). */
  showOwner?: boolean;
  pending: boolean;
  /** Отзыв действующего ключа. Без него и без onDelete колонки действий нет: в демо ключи не отзываются (DEMO-RULE-2). */
  onRevoke?(key: ApiKeyDto): void;
  /** Удаление отозванного ключа (IAM-UC-13, IAM-UC-13.CC-1). */
  onDelete?(key: ApiKeyDto): void;
}

/** IAM-UC-10, IAM-UC-11: ключи с началом, областями и последним использованием; отозванные — с датой. */
export function ApiKeyTable({ keys, showOwner = false, pending, onRevoke, onDelete }: ApiKeyTableProps): ReactElement {
  const { t, locale } = useT();
  const columns: DataColumn<ApiKeyDto>[] = [
    {
      key: 'name',
      header: t('identity.apiKeys.name'),
      primary: true,
      cell: (key) => (
        <>
          <strong>{key.name}</strong>
          <div className={`u-muted u-small ${styles.prefix}`}>{key.prefix}…</div>
        </>
      ),
    },
  ];
  if (showOwner) {
    columns.push({
      key: 'owner',
      header: t('identity.apiKeys.owner'),
      cell: (key) => (
        <>
          {key.owner.displayName}
          <div className="u-muted u-small">{key.owner.login}</div>
        </>
      ),
    });
  }
  columns.push(
    {
      key: 'scopes',
      header: t('identity.apiKeys.scopesLabel'),
      cell: (key) => (
        <div className="u-row u-gap-1">
          {key.scopes.map((scope) => (
            <Badge key={scope}>{t(`identity.apiKeys.scopeShort.${scope}`)}</Badge>
          ))}
        </div>
      ),
    },
    { key: 'created', header: t('identity.apiKeys.created'), cell: (key) => formatDateTime(key.createdAt, locale) },
    {
      key: 'lastUsed',
      header: t('identity.apiKeys.lastUsed'),
      cell: (key) => (key.lastUsedAt ? formatDateTime(key.lastUsedAt, locale) : <span className="u-muted">{t('identity.apiKeys.neverUsed')}</span>),
    },
    {
      key: 'status',
      header: t('identity.apiKeys.status'),
      cell: (key) =>
        isApiKeyActive(key) ? (
          <Badge tone="success">{t('identity.apiKeys.active')}</Badge>
        ) : (
          <Badge tone="neutral">{t('identity.apiKeys.revoked', { date: formatDateTime(key.revokedAt, locale) })}</Badge>
        ),
    },
  );
  if (onRevoke || onDelete) {
    columns.push({
      key: 'actions',
      header: t('identity.apiKeys.actions'),
      actions: true,
      cell: (key) => {
        if (isApiKeyActive(key)) {
          return onRevoke ? (
            <Button size="sm" variant="ghost" disabled={pending} onClick={() => onRevoke(key)}>
              {t('identity.apiKeys.revoke')}
            </Button>
          ) : null;
        }
        return onDelete ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onDelete(key)}>
            {t('common.delete')}
          </Button>
        ) : null;
      },
    });
  }
  return (
    <DataTable
      label={t('identity.apiKeys.title')}
      rows={keys}
      rowKey={(key) => key.id}
      columns={columns}
      empty={<EmptyState title={t('identity.apiKeys.empty')} text={t('identity.apiKeys.emptyText')} />}
    />
  );
}
